import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ToastContainer';
import './SvarraAdviceBot.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface SvarraAdviceBotProps {
  selectedAgentId?: string;
}

const SvarraAdviceBot: React.FC<SvarraAdviceBotProps> = ({ selectedAgentId }) => {
  const { user } = useAuth();
  const { showError } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load selected agent data
  useEffect(() => {
    const loadAgent = async () => {
      if (selectedAgentId) {
        console.log('🔍 Loading agent data for ID:', selectedAgentId);
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agents/${selectedAgentId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('🔍 Agent load response status:', response.status);
          if (response.ok) {
            const data = await response.json();
            console.log('🔍 Agent data loaded:', data.agent);
            setSelectedAgent(data.agent);
          } else {
            console.error('Failed to load agent - status:', response.status);
          }
        } catch (error) {
          console.error('Failed to load agent:', error);
        }
      } else {
        console.log('🔍 No selectedAgentId provided');
        setSelectedAgent(null);
      }
    };
    loadAgent();
  }, [selectedAgentId]);

  // Load messages from session storage on mount
  useEffect(() => {
    const storageKey = selectedAgent ? `svarra-advice-messages-${selectedAgent.id}` : 'svarra-advice-messages';
    const savedMessages = sessionStorage.getItem(storageKey);
    
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        setMessages(parsedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        })));
      } catch (error) {
        console.error('Failed to load saved messages:', error);
      }
    } else {
      // Add welcome message for new sessions
      const welcomeMessage = selectedAgent
        ? `Hi ${user?.firstName || 'there'}! I'm Svarra, your business advisor for ${selectedAgent.businessName}.

I'm here to help you grow your business with actionable advice on:
📈 Marketing strategies
💼 Operations optimization
📊 Growth strategies
💡 Customer retention
🎯 Sales & conversion

What business challenge can I help you with today?`
        : `Hi ${user?.firstName || 'there'}! I'm Svarra, your business advisor. I'm here to help you grow your business with actionable advice on marketing, operations, growth strategies, and more. What business challenge can I help you with today?`;

      setMessages([{
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date()
      }]);
    }
  }, [user?.firstName, selectedAgent]);

  // Save messages to session storage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      const storageKey = selectedAgent ? `svarra-advice-messages-${selectedAgent.id}` : 'svarra-advice-messages';
      sessionStorage.setItem(storageKey, JSON.stringify(messages));
    }
  }, [messages, selectedAgent]);

  // Scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: newMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Only call business advice endpoint - no appointment management
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/svarra-advice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage.content,
          conversationHistory
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get advice');
      }

      const data = await response.json();

      const assistantMessage: Message = {
        role: 'assistant',
        content: data.reply,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to get advice. Please try again.');

      // Add error message to chat
      const errorMessage: Message = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    const welcomeMessage = selectedAgent
      ? `Hi ${user?.firstName || 'there'}! I'm Svarra, your business advisor for ${selectedAgent.businessName}.

I'm here to help you grow your business with actionable advice on:
📈 Marketing strategies
💼 Operations optimization
📊 Growth strategies
💡 Customer retention
🎯 Sales & conversion

What business challenge can I help you with today?`
      : `Hi ${user?.firstName || 'there'}! I'm Svarra, your business advisor. I'm here to help you grow your business with actionable advice on marketing, operations, growth strategies, and more. What business challenge can I help you with today?`;

    setMessages([{
      role: 'assistant',
      content: welcomeMessage,
      timestamp: new Date()
    }]);
    
    const storageKey = selectedAgent ? `svarra-advice-messages-${selectedAgent.id}` : 'svarra-advice-messages';
    sessionStorage.removeItem(storageKey);
  };


  const suggestedQuestions = selectedAgent
    ? [
        "How can I improve customer acquisition?",
        "What marketing strategies work best for my business?",
        "How can I increase repeat customers?",
        "What are effective pricing strategies?"
      ]
    : [
        "How can I improve my customer acquisition?",
        "What marketing strategies work best for small businesses?",
        "How do I scale my operations efficiently?",
        "What are some ways to improve customer retention?"
      ];

  return (
    <div className="svarra-advice-bot">
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="svarra-bot-trigger"
          aria-label="Ask Svarra for business advice"
        >
          <div className="svarra-bot-icon">
            💡
          </div>
          <div className="svarra-bot-text">
            <div className="svarra-bot-title">Ask Svarra</div>
            <div className="svarra-bot-subtitle">for advice</div>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="svarra-bot-window">
          {/* Header */}
          <div className="svarra-bot-header">
            <div className="svarra-bot-header-info">
              <div className="svarra-bot-avatar">💡</div>
              <div>
                <div className="svarra-bot-name">Svarra - Your Business Advisor</div>
                <div className="svarra-bot-status">Ready to help</div>
              </div>
            </div>
            <div className="svarra-bot-controls">
              <button
                onClick={clearChat}
                className="svarra-bot-control-btn"
                title="Clear chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="svarra-bot-control-btn"
                title="Close chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="svarra-bot-messages">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`svarra-bot-message ${
                  message.role === 'user' ? 'svarra-bot-message-user' : 'svarra-bot-message-assistant'
                }`}
              >
                <div className="svarra-bot-message-content">
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {message.content}
                  </div>
                </div>
                <div className="svarra-bot-message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="svarra-bot-message svarra-bot-message-assistant">
                <div className="svarra-bot-message-content">
                  <div className="svarra-bot-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length === 1 && (
            <div className="svarra-bot-suggestions">
              <div className="svarra-bot-suggestions-title">Quick questions to get started:</div>
              {suggestedQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => setNewMessage(question)}
                  className="svarra-bot-suggestion"
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="svarra-bot-input">
            <div className="svarra-bot-input-container">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything about growing your business..."
                className="svarra-bot-textarea"
                rows={1}
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || isLoading}
                className="svarra-bot-send"
                title="Send message"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22,2 15,22 11,13 2,9"></polygon>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SvarraAdviceBot;